// Generated from scripts/overflow-list.ts. Rebuild with pnpm scripts:build.
import { DeclarativeShadowElement } from '@theme/component';
import { ResizeNotifier } from '@theme/utilities';
/**
 * Published on the list after a layout that could be measured, once per measurement, when the list has a
 * `minimum-items` attribute. `minimumReached` is the state the `minimum-reached` attribute now has.
 */
export class OverflowMinimumEvent extends CustomEvent {
    static eventName = 'overflowMinimum';
    constructor(minimumReached) {
        super(OverflowMinimumEvent.eventName, { bubbles: true, detail: { minimumReached } });
    }
}
/**
 * A list that moves the items that do not fit on its row into an overflow slot, behind a "More" control.
 * The header menu, the horizontal filters and the swatches of a product card use it.
 *
 * - `minimum-items`: when fewer than this many items stay visible on the row the element carries the
 *   `minimum-reached` attribute, and when they fit again the attribute is removed. Every measured layout publishes
 *   `overflowMinimum` (see `OverflowMinimumEvent`). A list that is not rendered (`display: none`, as the header
 *   menu is while the drawer takes its place) cannot be measured, so it keeps the state it last measured and
 *   publishes nothing; whoever hides it decides when to show it again, and the next layout settles the state.
 * - `disabled="true"` (`showAll()`): every item is visible and the layout stops.
 * - A bubbling `reflow` event on the element asks for a new layout; `detail.lastVisibleElement` is an item that
 *   takes the last visible place.
 *
 * @example
 * <overflow-list minimum-items="2">
 *   <!-- list items -->
 * </overflow-list>
 */
export class OverflowList extends DeclarativeShadowElement {
    static get observedAttributes() {
        return ['disabled', 'minimum-items'];
    }
    attributeChangedCallback(name, _oldValue, newValue) {
        // The attribute can be read before the shadow tree is ready; the first layout then covers it.
        if (name !== 'disabled' || !this.#parts)
            return;
        if (newValue === 'true') {
            this.#reset();
        }
        else {
            this.#reflowItems();
        }
    }
    async connectedCallback() {
        super.connectedCallback();
        // Styles for dynamically injected <overflow-list> elements are async.
        // We need to wait for them to be loaded before initializing the element to properly calculate the overflow.
        await this.#waitForStyles();
        if (this.isConnected)
            this.#initialize();
    }
    disconnectedCallback() {
        this.#lifetime?.abort();
        this.#lifetime = undefined;
        this.#resizeObserver.disconnect();
        this.#mutationObserver.disconnect();
        this.#intersectionObserver.disconnect();
    }
    #waitForStyles() {
        const styles = this.shadowRoot?.querySelector('link[rel="stylesheet"]');
        // No styles or styles are already loaded.
        if (!styles || styles.sheet)
            return Promise.resolve();
        return new Promise((resolve) => {
            styles.addEventListener('load', () => resolve(), { once: true });
        });
    }
    #initialize() {
        const { shadowRoot } = this;
        if (!shadowRoot)
            throw new Error('Missing shadow root');
        const defaultSlot = shadowRoot.querySelector('slot:not([name])');
        const overflowSlot = shadowRoot.querySelector('slot[name="overflow"]');
        const moreSlot = shadowRoot.querySelector('slot[name="more"]');
        const overflow = shadowRoot.querySelector('[part="overflow"]');
        const list = shadowRoot.querySelector('[part="list"]');
        const placeholder = shadowRoot.querySelector('[part="placeholder"]');
        if (!(defaultSlot instanceof HTMLSlotElement) ||
            !(overflowSlot instanceof HTMLSlotElement) ||
            !(moreSlot instanceof HTMLSlotElement) ||
            !(overflow instanceof HTMLElement) ||
            !(list instanceof HTMLUListElement) ||
            !(placeholder instanceof HTMLLIElement)) {
            throw new Error('Invalid element types in <OverflowList />');
        }
        this.#parts = { defaultSlot, overflowSlot, moreSlot, overflow, list, placeholder };
        this.#lifetime?.abort();
        this.#lifetime = new AbortController();
        this.addEventListener('reflow', this.#onReflowRequest, { signal: this.#lifetime.signal });
        // When <overflow-list> is dynamically injected, the browser doesn't remove its <template> automatically.
        // In theory, we could get rid of it now, or in DeclarativeShadowElement, but that would invalidate the layout.
        // Instead, we ignore it for now and remove it later on the first reflow.
        const elements = defaultSlot.assignedElements().filter((element) => !(element instanceof HTMLTemplateElement));
        const firstElement = elements[0];
        const lastElement = elements[elements.length - 1];
        // Observe the first and last elements to trigger a reflow when they are visible.
        // That way we can get their height from the IntersectionObserver for free (without reflows).
        if (firstElement) {
            this.#intersectionObserver.observe(firstElement);
        }
        if (lastElement && lastElement !== firstElement) {
            this.#intersectionObserver.observe(lastElement);
        }
    }
    #lifetime;
    #parts;
    #scheduled = false;
    /** The number of items that must stay visible before the list leaves the `minimum-reached` state, or null. */
    get minimumItems() {
        const value = this.getAttribute('minimum-items');
        return value ? parseInt(value, 10) : null;
    }
    get overflowSlot() {
        return this.#parts?.overflowSlot;
    }
    get defaultSlot() {
        return this.#parts?.defaultSlot;
    }
    #onReflowRequest = (event) => {
        const detail = event instanceof CustomEvent ? event.detail : undefined;
        const requested = typeof detail === 'object' && detail !== null && 'lastVisibleElement' in detail ? detail.lastVisibleElement : null;
        this.#reflowItems(0, requested instanceof HTMLElement ? requested : null);
    };
    #handleIntersection = (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting) {
            this.#intersectionObserver.disconnect();
            setTimeout(() => {
                // Remove the leftover <template> for dynamically injected <overflow-list> elements.
                this.querySelector(':scope > template[shadowrootmode="open"]')?.remove();
                this.#reflowItems(entry.boundingClientRect.height);
            }, 0);
        }
    };
    #handleChange = () => {
        if (this.#scheduled)
            return;
        this.#scheduled = true;
        requestAnimationFrame(() => setTimeout(() => {
            this.#reflowItems();
            this.#scheduled = false;
        }, 0));
    };
    /** Move all items to the default slot. */
    #moveItemsToDefaultSlot(parts) {
        const { defaultSlot, overflowSlot } = parts;
        for (const element of overflowSlot.assignedElements()) {
            if (element.slot !== defaultSlot.name) {
                element.slot = defaultSlot.name;
            }
        }
    }
    /** Reset the list to its initial state and disconnect the observers. */
    #reset() {
        const parts = this.#parts;
        if (!parts)
            return;
        this.#unobserveChanges();
        this.#moveItemsToDefaultSlot(parts);
        parts.list.style.removeProperty('height');
        this.style.setProperty('--overflow-count', '0');
    }
    /**
     * Sets or removes the `minimum-reached` attribute from the number of items that stayed visible on a measured
     * layout, and publishes the state.
     */
    #updateMinimumReached(visibleCount) {
        const { minimumItems } = this;
        if (minimumItems === null)
            return;
        const minimumReached = visibleCount < minimumItems;
        this.toggleAttribute('minimum-reached', minimumReached);
        this.dispatchEvent(new OverflowMinimumEvent(minimumReached));
    }
    /** Show all items in the list. */
    showAll() {
        const placeholder = this.#parts?.placeholder;
        if (!placeholder)
            return;
        placeholder.style.setProperty('width', '0');
        placeholder.style.setProperty('display', 'none');
        this.setAttribute('disabled', 'true');
    }
    /**
     * Reflow items based on available space within the list.
     * @param listHeight Initial height of the list
     * @param lastVisibleElement Optional element to place in last visible position
     */
    #reflowItems = (listHeight = 0, lastVisibleElement = null) => {
        const parts = this.#parts;
        if (!parts)
            return;
        const { defaultSlot, overflowSlot, moreSlot, list, placeholder } = parts;
        this.#unobserveChanges();
        // Reset all elements to the default slot so we can check which ones overflow.
        this.#moveItemsToDefaultSlot(parts);
        const elements = defaultSlot.assignedElements();
        const lastElement = elements[elements.length - 1];
        if (!lastElement) {
            this.#observeChanges();
            return;
        }
        const visibleElements = [];
        const overflowingElements = [];
        let placeholderWidth = 0;
        let hasOverflow = false;
        if (listHeight > 0) {
            list.style.setProperty('height', `${listHeight}px`);
        }
        // Enable flex-wrap so overflowing items break to the next line. This makes calculations easier.
        list.style.setProperty('flex-wrap', 'wrap');
        placeholder.hidden = true;
        // Putting the "More" item (and lastVisibleElement, if provided) at the start of the list lets us see which items will fit on the same row.
        moreSlot.style.setProperty('order', '-1');
        moreSlot.hidden = false;
        lastVisibleElement?.style.setProperty('order', '-1');
        const moreSlotRect = moreSlot.getBoundingClientRect();
        elements.forEach((element) => {
            const elementRect = element.getBoundingClientRect();
            if (elementRect.top > moreSlotRect.top) {
                if (!overflowingElements.length) {
                    placeholderWidth = elementRect.width;
                }
                hasOverflow = true;
                overflowingElements.push(element);
            }
            else {
                visibleElements.push(element);
            }
        });
        if (hasOverflow) {
            moreSlot.style.removeProperty('order');
        }
        lastVisibleElement?.style.removeProperty('order');
        // Move the elements to the correct slot.
        for (const element of elements) {
            const targetSlot = overflowingElements.includes(element) ? overflowSlot.name : defaultSlot.name;
            if (element.slot !== targetSlot) {
                element.slot = targetSlot;
            }
        }
        list.style.setProperty('counter-reset', `overflow-count ${overflowingElements.length}`);
        this.style.setProperty('--overflow-count', `${overflowingElements.length}`);
        // Adjust the "More" button visibility.
        moreSlot.hidden = !hasOverflow;
        if (hasOverflow) {
            // Set the width and height of the placeholder so the list can grow if there is space.
            placeholder.style.width = `${placeholderWidth}px`;
            placeholder.hidden = false;
        }
        // Reset the overflow property since children elements may need to display outside the list (e.g. dropdowns, popovers).
        list.style.setProperty('overflow', 'unset');
        // A list that is not rendered (display: none) has no boxes and measures nothing: every item reports a zero
        // rectangle, which looks like a row where everything fits. It keeps the state it last measured.
        if (list.getClientRects().length > 0)
            this.#updateMinimumReached(visibleElements.length);
        this.#observeChanges();
    };
    #observeChanges() {
        this.#resizeObserver.observe(this);
        this.#mutationObserver.observe(this, { childList: true });
    }
    #unobserveChanges() {
        this.#resizeObserver.disconnect();
        this.#mutationObserver.disconnect();
    }
    #resizeObserver = new ResizeNotifier(this.#handleChange);
    #mutationObserver = new MutationObserver(this.#handleChange);
    #intersectionObserver = new IntersectionObserver(this.#handleIntersection, {
        // Extend the root margin to around one more viewport of a typical mobile screen.
        rootMargin: '640px 360px 640px 360px',
    });
}
if (!customElements.get('overflow-list')) {
    customElements.define('overflow-list', OverflowList);
}
